<template>
  <div>
    <div class="main-info">
      <div class="main-info_header">
        <div class="main-info_header_left">
          <BaseSvgIcon
            v-if="hasValidIcon"
            :name="icon"
            class="main-info_header_icon"
          />
          <div class="main-info_header_titles">
            <Text
              class="main-info__title"
              pattern="body1"
              :weight="600"
              :height="110"
            >
              {{ $t("Amount") }}
            </Text>
          </div>
        </div>
      </div>
      <div class="main-info__balance">
        <div class="main-info__balance-amount">
          {{ amountData?.amount }}
        </div>
        <div class="main-info__balance-usd-value">
          {{ amountData?.usd_value }}
        </div>
      </div>
      <div v-if="reactiveDetails?.length > 0" class="main-info_details">
        <div
          v-for="{ id, label, value } in reactiveDetails"
          :key="id"
          class="main-info_details_item"
        >
          <div class="main-info_details_item_label">
            <Text pattern="body2" color="#787878" :weight="500" :height="150">
              {{ $t(label) }}
            </Text>
          </div>
          <div class="main-info_details_item_value">
            <Text
              v-if="label !== 'Hash'"
              pattern="body2"
              :weight="600"
              :height="135"
              :textDecoration="underline ? 'underline' : 'none'"
              class="main-info_details_item_value_text"
            >
              {{ value }}
            </Text>
            <div v-else class="hash_skeleton">
              <BaseSkeleton class="hash_skeleton_long" isNoAnimation />
              <BaseSkeleton class="hash_skeleton_short" isNoAnimation />
            </div>
          </div>
        </div>
        <div class="main-info_details_buttons">
          <BaseButton
            type="shade"
            outline
            disabled
            class="main-info_details_button"
          >
            <BaseSvgIcon name="copy" class="icon" />
            {{ $t("Transaction | Copy hash") }}
          </BaseButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";

// components
import { Text, BaseSvgIcon, BaseButton } from "@/shared/ui";
import BaseSkeleton from "@/shared/ui/BaseSkeleton/BaseSkeleton.vue";

// icons
import "@/assets/icons/SVG/bitcoin.svg";
import "@/assets/icons/SVG/tron.svg";
import "@/assets/icons/SVG/usdt.svg";
import "@/assets/icons/SVG/colored-check-green.svg";
import "@/assets/icons/SVG/pending.svg";
import "@/assets/icons/SVG/error2.svg";
import "@/assets/icons/SVG/success2.svg";
import "@/assets/icons/SVG/info-gray.svg";
import "@/assets/icons/SVG/copy.svg";
import "@/assets/icons/SVG/share.svg";

import { NETWORK_ENUMS } from "@/shared/helpers";

// props
const props = defineProps({
  icon: {
    type: String,
    default: "",
    validator: (value) =>
      [NETWORK_ENUMS.bitcoin, NETWORK_ENUMS.tron, NETWORK_ENUMS.usdt].includes(
        value,
      ),
  },
  details: {
    type: Array,
    default: () => [],
  },
  amountData: {
    type: Object,
    default: null,
  },
  loading: {
    type: Boolean,
    default: true,
  },
});

// computed
const hasValidIcon = computed(() => {
  const availableIcons = [
    NETWORK_ENUMS.bitcoin,
    NETWORK_ENUMS.tron,
    NETWORK_ENUMS.usdt,
  ];
  return props.icon && availableIcons.includes(props.icon);
});

// Реактивное форматирование деталей с учетом текущей локали
const reactiveDetails = computed(() => {
  if (!props.details || !Array.isArray(props.details)) {
    return [];
  }

  return props.details.map((detail) => {
    return detail;
  });
});
</script>

<style lang="scss" scoped>
.main-info {
  display: flex;
  flex-direction: column;
  background: var(--Dark-900, #111);
  width: 100%;
  border-radius: 10px;
  border: 1px solid var(--Dark-850, #1a1a1a);
  overflow: hidden;

  &__title {
    color: #7a7a7a !important;
    font-size: 14px;
    font-weight: 500 !important;
    line-height: 150% !important;
    margin-bottom: 4px;
  }

  &__balance {
    padding: 0 32px;

    &-amount {
      font-size: 20px;
      font-weight: 600;
      line-height: 150%;
      color: #fafafa;
    }

    &-usd-value {
      font-size: 14px;
      font-weight: 500;
      line-height: 150%;
      color: #7a7a7a;
    }
  }

  &_header {
    position: relative;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
    padding: 32px 32px 16px;

    &_left {
      display: flex;
      flex-direction: row;
      justify-content: flex-start;
      align-items: center;
      gap: 13px;
    }

    &_titles {
      display: flex;
      flex-direction: column;
      justify-content: center;
      gap: 4px;
      margin-right: 12px;
    }

    &_contract {
      display: flex;
      flex-direction: row;
      align-items: center;
      padding: 6px 6.5px;
      gap: 6px;
      border-radius: 50px;
      background: var(--green-850);
      height: fit-content;

      &_icon {
        width: 23px;
        height: 23px;
      }
    }

    &_icon {
      position: absolute;
      right: 32px;
      top: 32px;
      width: 36px;
      height: 36px;
      pointer-events: none;
    }
  }

  &_details {
    display: flex;
    flex-direction: column;
    gap: 20px;
    width: 100%;
    padding: 20px 32px 32px;

    &_buttons {
      display: flex;
      flex-direction: row;
      justify-content: center;
      align-items: center;
      gap: 8px;
    }

    &_button {
      width: 100%;
      justify-items: center;
      align-items: center;
      padding: 11px 2px;
      height: 36px;
      color: var(--Basic-White, #fff);
      text-align: center;
      font-size: 14px;
      font-style: normal;
      font-weight: 500;
      line-height: 106%;
      gap: 10px;
      background-color: #1a1a1a;
      border-radius: 10px;
      border: none;

      .icon {
        width: 16px;
        height: 16px;
        pointer-events: none;
      }
    }

    &_item {
      display: flex;
      flex-direction: column;

      &_label {
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        align-items: center;
        gap: 3px;
        margin-bottom: 4px;
      }

      &_value {
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        gap: 8px;

        .annotation {
          white-space: nowrap;
        }
      }

      &_value_text {
        font-weight: 500 !important;
        line-height: 150% !important;
      }

      &_links {
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        gap: 4px;
      }

      .underline {
        text-decoration: underline;
      }

      .status-row {
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        align-items: center;
        gap: 4px;
      }

      .copy {
        min-width: 20px;
        min-height: 20px;
        transition: color 0.3s;
        cursor: pointer;
        color: #c18afd;

        &:hover {
          color: #fff;
        }
      }

      .contract-label {
        user-select: none;
        border-radius: 11px;
        border: 1px solid var(--Dark-700, #2d2d2d);
        padding: 3px 8px;
      }
    }
  }
}

.hash_skeleton {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.hash_skeleton_long {
  margin-bottom: 4px;
  width: 250px;
  height: 17px;
  border-radius: 10px;
}

.hash_skeleton_short {
  width: 150px;
  height: 17px;
  border-radius: 10px;
}
</style>
